import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { LookupModel } from '../../api/models';
import { MatFormFieldControl, MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher, MatOption } from '@angular/material/core';
import { MatBaseInputComponent } from '../mat-base-input.component';
import { MatFormFieldFix } from '../mat-form-field/mat-form-field-fix';
import { NgClass, NgFor } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';

@Component({
    selector: 'app-mat-autocomplete',
    template: `
    <mat-form-field
      appearance="outline"
      class="regular-form-field"
      [ngClass]="{ 'no-label': hideLabel }"
      [hintLabel]="hintLabel"
      [hideRequiredMarker]="hideLabel"
      (focusin)="onFocusIn($event)"
      (focusout)="onFocusOut($event)"
    >
      <mat-label>{{ label }}</mat-label>
      <input
        matInput
        type="text"
        #inputTrigger
        (ngModelChange)="valueChanged.emit($event)"
        [matAutocomplete]="autoComplete"
        [formControl]="formControl"
      />

      <mat-autocomplete #autoComplete="matAutocomplete" [displayWith]="displayFn.bind(this)">
        <mat-option *ngFor="let item of items" [value]="item.id" [disabled]="item.text.indexOf('invalid') > -1">
          {{ item.text }}
        </mat-option>
      </mat-autocomplete>
      <mat-error>{{ firstError() }}</mat-error>
    </mat-form-field>
  `,
    styleUrls: ['./mat-autocomplete.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: MatAutocompleteComponent },
        { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    ],
    standalone: true,
    imports: [
        MatFormField,
        MatFormFieldFix,
        NgClass,
        MatLabel,
        MatInput,
        MatAutocompleteTrigger,
        FormsModule,
        ReactiveFormsModule,
        MatAutocomplete,
        NgFor,
        MatOption,
        MatError,
    ],
})
export class MatAutocompleteComponent extends MatBaseInputComponent implements OnInit {
  @Input() allowCustomValue = false;
  @Input() autoCompleteValues: LookupModel[] = [];

  @ViewChild('inputTrigger') inputTrigger: ElementRef<HTMLInputElement>;

  items: LookupModel[] = [];
  lastFilter = '';

  constructor(@Optional() @Self() public ngControl: NgControl, public elementRef: ElementRef<HTMLElement>) {
    super(ngControl, elementRef);
  }

  ngOnInit() {
    this.formControl?.valueChanges
      .pipe(
        startWith<string | LookupModel[]>(''),
        map(value => (typeof value === 'string' ? value : this.lastFilter)),
        map(searchText => {
          return this.filter(searchText).pipe(
            tap(data => {
              console.log('on search');
              if (searchText) {
                const newItemText = searchText.trim();
                if (newItemText && this.allowCustomValue) {
                  data.splice(0, 0, {
                    id: newItemText,
                    text: newItemText,
                  });
                }
              }
            }),
          );
        }),
      )
      .subscribe(data => {
        data.subscribe(items => (this.items = items));
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes?.['autoCompleteValues']) {
      this.filter(this.lastFilter).subscribe(items => {
        this.items = items;
      });
    }
  }

  filter(searchText: string): Observable<LookupModel[]> {
    return of(
      this.autoCompleteValues.filter(x => (x.text || '').toLowerCase().indexOf((searchText || '').toLowerCase()) > -1),
    );
  }

  displayFn(item): string {
    const foundValue = this.autoCompleteValues?.find(x => x.id === item);

    return foundValue?.text || item || '';
  }
}
